import { useEffect, useState } from "react";
import type { PatientProcedure, VisitSummary } from "@dentacore/shared-types";

import { listPatientProcedures } from "../api/patients";
import { Pager, useFitPages } from "../ui/paging";
import { counted, DAY_GAP, DayCard, DayList, dayLabel } from "./by-day";
import styles from "./history-tab.module.css";
import { countsOf, visitRowData } from "./visit-dialog";

/**
 * Everything that has happened to this patient, newest first — **the visits
 * and the work charted at them, in one place** (PF5, 2026-09-27: *"merge visits
 * and history"*).
 *
 * ── Why the chart was not already this ──────────────────────────────────────
 *
 * The tooth chart answers *what is in this mouth now*. It is a drawing of the
 * present, and reading a history out of it means opening thirty-two teeth one
 * at a time. The question a clinician actually asks between visits is *what
 * have we done for this person*, which is chronological and crosses teeth. The
 * client: *"the history of the work done for the patient does not show in the
 * patient's record."*
 *
 * ── One container per day, because that is how it happened ─────────────────
 *
 * A day's visit sits at the top of its container and opens the visit dialog —
 * the note, the invoice and the images from that date, and *Complete visit and
 * bill it*. The procedures charted that day sit under it. A day can have a visit
 * and no charted work (a cleaning), or charted work and no visit (history typed
 * in from paper); both still get a container.
 *
 * **Cancelled procedures are shown and marked**, for the same reason the
 * tooth's own history keeps them: a planned procedure decided against is a
 * clinical fact.
 */
export function HistoryTab({
  patientId,
  visits,
  version = 0,
  onOpenVisit,
}: {
  patientId: string;
  /** The file's visits, loaded once for the whole file (PF12). */
  visits: readonly VisitSummary[] | null;
  /** Bumped when work was recorded elsewhere — *Add work done* (EB7) — so this re-reads. */
  version?: number;
  onOpenVisit: (date: string) => void;
}): React.JSX.Element {
  const [procedures, setProcedures] = useState<readonly PatientProcedure[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setProcedures(null);
    setFailed(false);

    listPatientProcedures(patientId, controller.signal)
      .then(setProcedures)
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => controller.abort();
  }, [patientId, version]);

  const days = procedures === null || visits === null ? [] : historyDays(visits, procedures);

  const pages = useFitPages({
    keys: days.map((day) => day.key),
    // A visit is a 50px line and a procedure a 44px one.
    estimate: (index) => {
      const day = days[index];
      return day === undefined ? 62 : Math.max(62, 50 * day.visits.length + 44 * day.work.length);
    },
    gap: DAY_GAP,
    resetOn: patientId,
  });
  const [start, end] = pages.range;

  if (failed) {
    return (
      <p className={styles.quiet} data-testid="history-failed">
        Could not read this patient&rsquo;s history. Reload to try again.
      </p>
    );
  }

  if (procedures === null || visits === null) {
    return <p className={styles.quiet}>Reading&hellip;</p>;
  }

  if (days.length === 0) {
    return (
      <p className={styles.well} data-testid="history-empty">
        No visits or charted work yet. A visit appears here once an appointment has happened, with
        whatever was recorded on the tooth chart that day.
      </p>
    );
  }

  return (
    <div className={styles.history} data-testid="history-tab">
      <p className={styles.intro}>
        {counted(visits.length, "visit")} and {counted(procedures.length, "procedure")}, newest
        first
      </p>

      <div className={styles.area} ref={pages.areaRef}>
        <DayList testId="history-days">
          {days.slice(start, end).map((day) => (
            <DayCard
              key={day.key}
              dayKey={day.key}
              label={dayLabel(day.key)}
              count={dayCount(day)}
              measureRef={pages.measure(day.key)}
              flush
            >
              {day.visits.map((visit) => (
                <button
                  key={visit.date}
                  type="button"
                  className={styles.visit}
                  onClick={() => onOpenVisit(visit.date)}
                  data-card=""
                  data-testid="visit-row"
                  {...visitRowData(visit)}
                >
                  <span className={styles.visitTitle}>{visit.title ?? "Visit"}</span>
                  <span className={styles.visitCounts}>{countsOf(visit)}</span>
                  <span className={styles.visitBy}>
                    {visit.providerName ?? visit.branchName ?? ""}
                  </span>
                </button>
              ))}

              {day.work.length > 0 && (
                <ul className={styles.rows}>
                  {day.work.map((row) => (
                    <li
                      key={row.id}
                      className={row.status === "CANCELLED" ? styles.rowCancelled : styles.row}
                      data-testid="history-row"
                    >
                      {/*
                        **`#15`, not `15`** (client, 2026-09-07: *"the numbers
                        next to a procedure are confusing, what is 15"*). The
                        hash is how the plans screen writes a tooth, and the
                        name sits beside it, so the row explains itself.
                      */}
                      {row.fdiNumber === null ? (
                        // Whole-mouth work (EB6): no tooth to number, and the
                        // name beside it says so.
                        <span className={styles.tooth} aria-hidden="true">
                          &mdash;
                        </span>
                      ) : (
                        <span
                          className={styles.tooth}
                          aria-label={`Tooth ${String(row.fdiNumber)}`}
                        >
                          #{row.fdiNumber}
                        </span>
                      )}
                      <span className={styles.label}>{row.label}</span>
                      <span className={styles.meta}>
                        {row.toothName}
                        {row.surfaces.length > 0 && ` · ${row.surfaces.join("")}`}
                      </span>
                      <span className={styles.meta}>{row.performedBy ?? ""}</span>
                      {/* State in words as well as in colour. */}
                      <span
                        className={row.status === "PLANNED" ? styles.statePlanned : styles.state}
                      >
                        {row.status === "COMPLETED"
                          ? "Done"
                          : row.status === "PLANNED"
                            ? "Planned"
                            : "Cancelled"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </DayCard>
          ))}
        </DayList>
      </div>

      <Pager
        page={pages.page}
        count={pages.pages.length}
        onPage={pages.setPage}
        from={start + 1}
        to={end}
        total={days.length}
        noun="days"
        testId="history-pager"
      />
    </div>
  );
}

interface HistoryDay {
  /** `YYYY-MM-DD`. */
  key: string;
  visits: VisitSummary[];
  work: PatientProcedure[];
}

/** Visits and procedures, one entry per day, newest day first. */
function historyDays(
  visits: readonly VisitSummary[],
  procedures: readonly PatientProcedure[],
): HistoryDay[] {
  const days = new Map<string, HistoryDay>();
  const dayFor = (key: string): HistoryDay => {
    let day = days.get(key);
    if (day === undefined) {
      day = { key, visits: [], work: [] };
      days.set(key, day);
    }
    return day;
  };

  for (const visit of visits) dayFor(visit.date).visits.push(visit);
  for (const row of procedures) dayFor(dayOf(row).slice(0, 10)).work.push(row);

  return [...days.values()].sort((a, b) => b.key.localeCompare(a.key));
}

/** What the date cell counts: the procedures, or that it was a visit. */
function dayCount(day: HistoryDay): string {
  if (day.work.length > 0) return counted(day.work.length, "procedure");
  return counted(day.visits.length, "visit");
}

/**
 * Which day a procedure belongs to.
 *
 * **The visit's date where there is one**, and when the row was created
 * otherwise: a procedure charted at a visit belongs to that visit's day even if
 * it was written up that evening, and one charted with no visit — a correction,
 * or history entered from paper — has only the day it was typed.
 *
 * A planned procedure has no date of its own at all; it groups under the day it
 * was planned, which is the only honest answer.
 */
function dayOf(row: PatientProcedure): string {
  return row.visitDate ?? row.performedAt ?? row.createdAt;
}
